import { CsvTemplate } from '@/types'
import { listColumns } from '@/utils/csvTemplate'
import { Disclosure, Transition } from '@headlessui/react'

type Props = {
  template?: CsvTemplate
  isLoading?: boolean
}

export const CsvTemplateColumnReference = ({ template, isLoading }: Props) => {
  if (isLoading) {
    return <p className="text-xs text-navy-900 text-opacity-50">Loading column reference…</p>
  }
  if (!template) return null

  const columns = listColumns(template)
  if (columns.length === 0) return null

  return (
    <Disclosure>
      {({ open }) => (
        <div className="border border-navy-900 border-opacity-10 rounded-lg">
          <Disclosure.Button className="w-full flex justify-between items-center px-3 py-2 text-xs font-semibold text-navy-900 hover:bg-navy-900 hover:bg-opacity-5 rounded-lg">
            <span>
              {open ? 'Hide' : 'Show'} column reference ({columns.length} columns)
            </span>
            <span className="text-navy-900 text-opacity-60">{open ? '−' : '+'}</span>
          </Disclosure.Button>
          <Transition
            enter="transition duration-150 ease-out"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition duration-100 ease-in"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Disclosure.Panel className="px-3 pb-3 pt-1">
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-xs text-left text-navy-900">
                  <thead className="text-navy-900 text-opacity-60 border-b border-navy-900 border-opacity-10">
                    <tr>
                      <th className="py-1 pr-3 font-medium">Column</th>
                      <th className="py-1 pr-3 font-medium">Type</th>
                      <th className="py-1 pr-3 font-medium">Required</th>
                      <th className="py-1 pr-3 font-medium">Example</th>
                      <th className="py-1 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {columns.map(({ name, property, required }) => {
                      const isNumericType = property.type === 'integer' || property.type === 'number'
                      const typeLabel = [property.type, !isNumericType && property.format].filter(Boolean).join(' / ')
                      const example =
                        property.example === null || property.example === undefined ? '' : String(property.example)
                      return (
                        <tr key={name} className="border-b border-navy-900 border-opacity-5 last:border-b-0">
                          <td className="py-1 pr-3 font-mono whitespace-nowrap">{name}</td>
                          <td className="py-1 pr-3 text-navy-900 text-opacity-70 whitespace-nowrap">
                            {typeLabel || '—'}
                          </td>
                          <td className="py-1 pr-3 whitespace-nowrap">
                            {required ? <span className="text-primary-500 font-semibold">Yes</span> : '—'}
                          </td>
                          <td className="py-1 pr-3 font-mono text-navy-900 text-opacity-70 whitespace-nowrap">
                            {example}
                          </td>
                          <td className="py-1 text-navy-900 text-opacity-70">{property.description ?? ''}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </Disclosure.Panel>
          </Transition>
        </div>
      )}
    </Disclosure>
  )
}
